import React, { useState, useCallback } from "react"
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Tooltip
} from "@mui/material"
import styled from "@emotion/styled"
import { useDispatch } from "react-redux"
import InfoIcon from "@mui/icons-material/Info"
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward"
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward"

import styles from "./UsageMatricsTable.module.scss"
import avatar from "assets/images/user/avatarUser.svg"
import TableNoRecords from "components/TableNoRecords"
import AppPagination from "components/StyledComponents/AppPagination"
import { appActions } from "redux/app/slice/app"
import ProfileDisplayer from "components/ProfileDisplayer"
import AppButton from "components/StyledComponents/AppButton"
import CheckBoxBlueIcon from "components/CheckBoxBlue"

const CustomTable = styled(Table)(({ theme }) => ({
  backgroundColor: theme.palette.background.main,
  borderCollapse: "collapse !important",
  "& .MuiTableCell-root": {
    padding: "12px 24px 12px 0px",
    backgroundColor: theme.palette.background.main,
    borderBottom: "none",
    boxSizing: "border-box",
    "&:last-child": { paddingRight: 0 }
  },
  "& .MuiTableRow-root": {
    borderBottom: `2px solid ${theme.palette.background.default}`
  },
  "& .MuiTableCell-head": {
    position: "relative",
    padding: "24px 24px 12px 0px",
    "&:last-child": { paddingRight: 0 }
  }
}))

const renderCellContent = (
  column,
  index,
  currentPage,
  row,
  handleNotifyAll,
  selectedUserIds,
  handleCheckboxChange
) => {
  switch (column.id) {
    case "checkbox":
      return (
        <CheckBoxBlueIcon
          style={{ width: 24 }}
          checked={selectedUserIds.includes(row.user_id)}
          onClick={() => handleCheckboxChange(row.user_id)}
        />
      )
    case "id":
      return <span className={styles.rank}>{currentPage * 12 - (12 - 1) + index}</span>
    case "name":
      return (
        <div className={styles.tableAvatar}>
          {row?.image_url ? (
            <ProfileDisplayer image={row?.image_url} />
          ) : (
            <div className={styles.tableAvatar__img}>
              <img src={avatar} />
            </div>
          )}
          <div className="d-flex flex-row align-items-center gap-3">
            <Typography variant="body2Regular">{row.name}</Typography>
          </div>
        </div>
      )
    case "action":
      return (
        <div className={`${styles.tableAvatar} justify-content-center`}>
          <AppButton sx={{ padding: "13px 36px" }} onClick={() => handleNotifyAll(row)}>
            Notify
          </AppButton>
        </div>
      )
    default:
      return row[column.id] ?? "-"
  }
}

const UsageMatricsTable = ({
  columns,
  rowsData = [],
  setCurrentPage,
  currentPage,
  totalPage,
  isAwol,
  handleNotifyAll,
  selectedUserIds,
  handleCheckboxChange,
  handleSelectAllChange,
  usageMetricsList
}) => {
  const dispatch = useDispatch()
  const [sort, setSort] = useState("")
  const [sortDirection, setSortDirection] = useState("")

  const allUserIds = usageMetricsList.map((row) => row?.user_id)

  const isAllSelected =
    allUserIds?.length > 0 && allUserIds?.every((id) => selectedUserIds?.includes(id))

  const onHeaderCheckboxChange = () => {
    handleSelectAllChange(!isAllSelected)
  }

  const handleSortDirection = useCallback((dir, id) => {
    setSortDirection(dir)
    handleSort(dir, id)
  }, [])

  const handleSort = useCallback((dir, id) => {
    const order = dir === "asc" ? id : `-${id}`
    setSort(order)
    dispatch(appActions.setOrderBy(order))
  }, [])

  const firstColumn = isAwol ? [columns[0]] : []
  const alwaysVisibleColumns = columns
    ?.slice(1, 6)
    ?.filter((col) => !(isAwol && col?.id === "streak"))
  const filteredRestColumns = columns
    .slice(6)
    .filter((col) => (isAwol ? col.isHidden : !col.isHidden))
  const filteredColumns = [...firstColumn, ...alwaysVisibleColumns, ...filteredRestColumns]

  return (
    <>
      <Paper className={styles.tableContainer}>
        <TableContainer>
          <CustomTable stickyHeader aria-label="leaderboard table">
            <TableHead>
              <TableRow>
                {filteredColumns?.map(({ id, align, minWidth, tooltip, isSorting, label }) => (
                  <TableCell key={id} align={align} style={{ minWidth }}>
                    {label === "checkbox" ? (
                      <CheckBoxBlueIcon
                        role="button"
                        style={{ width: 24 }}
                        checked={isAllSelected}
                        onClick={onHeaderCheckboxChange}
                      />
                    ) : (
                      <Typography variant="body2Regular" className={styles.headerCell}>
                        {label}
                        {tooltip && (
                          <Tooltip title={tooltip}>
                            <InfoIcon fontSize="small" />
                          </Tooltip>
                        )}
                        {isSorting && (
                          <span className={styles.cursorPointer}>
                            {sortDirection === "desc" && sort === `-${id}` ? (
                              <ArrowDownwardIcon
                                onClick={() => handleSortDirection("asc", id)}
                                fontSize="small"
                              />
                            ) : (
                              <ArrowUpwardIcon
                                onClick={() => handleSortDirection("desc", id)}
                                fontSize="small"
                              />
                            )}
                          </span>
                        )}
                      </Typography>
                    )}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {rowsData?.length === 0 ? (
                <TableNoRecords colSpan={filteredColumns?.length} label="No Data Found" />
              ) : (
                rowsData?.map((row, index) => (
                  <TableRow key={row.id + index.toString()}>
                    {filteredColumns?.map((column) => (
                      <TableCell key={column.id} align={column.align || "left"}>
                        {renderCellContent(
                          column,
                          index,
                          currentPage,
                          row,
                          handleNotifyAll,
                          selectedUserIds,
                          handleCheckboxChange
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              )}
            </TableBody>
          </CustomTable>
        </TableContainer>
      </Paper>
      {rowsData?.length > 0 && (
        <AppPagination
          onPageChange={setCurrentPage}
          totalPages={totalPage}
          currentPage={currentPage}
        />
      )}
    </>
  )
}

export default UsageMatricsTable
